import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { siteData } from "../data/mock";

export const TestimonialsSection = () => {
  const { testimonials, clinic } = siteData;
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const next = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  };

  const prev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
  };

  return (
    <section id="testimonials" className="py-20 md:py-28" style={{ background: "#FFF9F2" }} aria-labelledby="testimonials-heading">
      <div className="max-w-[1280px] mx-auto px-5 md:px-9">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#999999] block mb-3">
              Testimonials
            </span>
            <h2
              id="testimonials-heading"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                lineHeight: 1.15,
                fontWeight: 600,
                color: "#232323",
                letterSpacing: "-0.01em",
              }}
            >
              What Our Patients Say
            </h2>
          </motion.div>

          {/* Yelp badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-3 rounded-full px-5 py-2.5"
            style={{
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(0,0,0,0.06)",
              backdropFilter: "blur(4px)",
            }}
          >
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={15} fill="#BCA182" stroke="#BCA182" />
              ))}
            </div>
            <span
              className="font-mono text-xs uppercase tracking-wider text-[#232323] font-medium"
            >
              {clinic.rating} on {clinic.ratingSource}
            </span>
          </motion.div>
        </div>

        {/* Testimonial carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative bg-white rounded-2xl p-8 md:p-12"
          role="region"
          aria-roledescription="carousel"
          aria-label="Patient testimonials"
          style={{
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
            border: "1px solid rgba(0,0,0,0.04)",
            minHeight: "260px",
          }}
        >
          <Quote
            size={40}
            strokeWidth={1}
            className="absolute top-6 left-6 md:top-8 md:left-10"
            style={{ color: "rgba(188,161,130,0.2)" }}
            aria-hidden="true"
          />

          <div className="relative overflow-hidden" style={{ minHeight: "160px" }} aria-live="polite" aria-atomic="true">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={current}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="flex flex-col items-center text-center"
                role="group"
                aria-roledescription="slide"
                aria-label={`Testimonial ${current + 1} of ${testimonials.length} by ${testimonials[current].name}`}
              >
                <div className="flex gap-1 mb-5" role="img" aria-label={`${testimonials[current].rating} out of 5 stars`}>
                  {[...Array(testimonials[current].rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#BCA182" stroke="#BCA182" />
                  ))}
                </div>
                <blockquote
                  className="max-w-[640px] mb-6"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: "clamp(1.15rem, 2.5vw, 1.5rem)",
                    lineHeight: 1.55,
                    fontWeight: 500,
                    color: "#232323",
                    fontStyle: "italic",
                  }}
                >
                  "{testimonials[current].quote}"
                </blockquote>
                <p
                  className="font-mono text-xs uppercase tracking-widest text-[#999999]"
                >
                  {testimonials[current].name}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-transparent cursor-pointer hover:bg-[#232323] hover:text-white transition-colors duration-200 text-[#232323]"
              style={{ border: "1px solid rgba(0,0,0,0.1)" }}
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setDirection(i > current ? 1 : -1);
                    setCurrent(i);
                  }}
                  className="border-none bg-transparent cursor-pointer p-0"
                  aria-label={`Go to testimonial ${i + 1}`}
                >
                  <div
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: i === current ? "24px" : "6px",
                      height: "6px",
                      background: i === current ? "#232323" : "rgba(0,0,0,0.15)",
                    }}
                  />
                </button>
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-transparent cursor-pointer hover:bg-[#232323] hover:text-white transition-colors duration-200 text-[#232323]"
              style={{ border: "1px solid rgba(0,0,0,0.1)" }}
              aria-label="Next testimonial"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </motion.div>

        {/* Mini testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {testimonials.slice(0, 3).map((t, index) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-xl p-5"
              style={{
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div className="flex gap-0.5 mb-2.5" role="img" aria-label={`${t.rating} out of 5 stars`}>
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={i} size={11} fill="#BCA182" stroke="#BCA182" />
                ))}
              </div>
              <p
                className="mb-2.5"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.82rem",
                  lineHeight: 1.55,
                  color: "#353535",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {t.quote}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#999999] m-0">
                {t.name}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
